import { api } from "./api";

const base = (orgId: string) => `/org/${orgId}/loxley`;

export type LoxleyStatus = {
  enabled: boolean;
  has_seat: boolean;
  is_org_admin: boolean;
  ai_enabled: boolean;
  monthly_command_limit: number;
  used_this_month: number;
  /** Platform MCP switch, the add-on and the org admin's switch all on. */
  mcp_available: boolean;
  mcp_url: string;
};

export type LoxleyActionSummary = {
  title: string;
  tab: string;
  fields: Array<[string, string]>;
  note: string;
  /** High risk: only this app can approve it, never an external AI app. */
  in_app_only: boolean;
};

export type LoxleyAction = {
  id: string;
  tool: string;
  risk: "write" | "high";
  channel: "app" | "mcp";
  status: "pending" | "running" | "done" | "failed" | "cancelled";
  summary: LoxleyActionSummary;
  result: { status: number; body: unknown } | null;
  created_at: string | null;
  expires_at: string | null;
};

export type LoxleyChatMessage = { role: "user" | "assistant"; content: string };

/** A record added to the chat. Only the type and id are sent: the server reads the record itself. */
export type LoxleyPin = { kind: string; id: string };

/** A button Loxley put under its reply: start a guided tour, or open a help article. */
export type LoxleyGuide =
  | { kind: "tour"; id: string; title: string }
  | { kind: "article"; guide: string; id: string; title: string };

/** A chart Loxley drew under its reply. The server worked out every value from the CRM's own results. */
export type LoxleyChart = {
  kind: "bar" | "stacked_bar" | "line" | "pie";
  title: string;
  labels: string[];
  series: { name: string; values: number[] }[];
  /** Currency code when every value is money in one currency. */
  unit: string | null;
  /** What the values are, stated by the server ("Sum of target, by display_name"), not by the model. */
  measure: string;
  caption: string;
};

export type LoxleyTurn = {
  /** Row in the turn log; feedback is sent against it. */
  turn_id: string;
  created_at: string;
  reply: string;
  actions: LoxleyAction[];
  guides?: LoxleyGuide[];
  charts?: LoxleyChart[];
  remaining_this_month: number;
  /** The guard refused the request before the model saw it (jailbreak or SQL injection). */
  blocked?: boolean;
};

export type SavedPrompt = { id: string; prompt: string; created_at: string };

export type FeedbackReason = "wrong" | "incomplete" | "not_helpful" | "wrong_record" | "too_slow" | "unsafe" | "other";

export type ConnectorKey = {
  id: string;
  user_id: string;
  email: string | null;
  kind: "api_key" | "oauth_refresh";
  name: string;
  prefix: string;
  scope: "read" | "read_write";
  client_id: string | null;
  created_at: string | null;
  expires_at: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
};

export type CreatedConnectorKey = ConnectorKey & { secret: string };

export type LoxleySeat = {
  user_id: string;
  email: string;
  display_name: string | null;
  has_seat: boolean;
  assigned_at: string | null;
  /** This month: in-app requests and the model tokens they used. */
  usage: { requests: number; input_tokens: number; output_tokens: number };
};

export type GuardrailOp = "read" | "create" | "update" | "delete" | "send";

/** Only the boxes that are off are stored; a missing entry means allowed. */
export type GuardrailRules = Record<string, Partial<Record<GuardrailOp, boolean>>>;

export type LoxleyGuardrails = {
  /** Staff have switched the Loxley add-on on for this organization. */
  enabled: boolean;
  /** Each tab, with the operations some Loxley tool actually performs there. */
  tabs: Array<{ key: string; label: string; ops: GuardrailOp[] }>;
  rules: GuardrailRules;
};

export type LoxleySettings = {
  enabled: boolean;
  seats: number;
  seats_assigned: number;
  monthly_command_limit: number;
  connectors_enabled: boolean;
  updated_at: string | null;
};

export type LoxleyAdmin = {
  settings: LoxleySettings;
  mcp_global: boolean;
  seats: LoxleySeat[];
  keys: ConnectorKey[];
  usage: { app_turns: number; app_tool_calls: number; mcp_tool_calls: number };
};

export type OAuthDetails = {
  client_name: string;
  redirect_host: string;
  scope: "read" | "read_write";
  allowed: boolean;
  reason: string;
};

export const loxleyApi = {
  status: (orgId: string) => api.get<LoxleyStatus>(`${base(orgId)}/status`),
  chat: (orgId: string, message: string, history: LoxleyChatMessage[], page: string | null,
    pinned: LoxleyPin[] = []) =>
    api.post<LoxleyTurn>(`${base(orgId)}/chat`, { message, history, page, pinned }),
  feedback: (orgId: string, turnId: string, rating: "up" | "down", reason?: FeedbackReason | null, comment?: string) =>
    api.post<{ saved: boolean }>(`${base(orgId)}/turns/${turnId}/feedback`, {
      rating, reason: reason ?? null, comment: comment?.trim() ? comment.trim() : null,
    }),
  savedPrompts: (orgId: string) => api.get<SavedPrompt[]>(`${base(orgId)}/saved-prompts`),
  savePrompt: (orgId: string, prompt: string) => api.post<SavedPrompt>(`${base(orgId)}/saved-prompts`, { prompt }),
  deletePrompt: (orgId: string, id: string) => api.delete<{ deleted: boolean }>(`${base(orgId)}/saved-prompts/${id}`),
  pending: (orgId: string) => api.get<LoxleyAction[]>(`${base(orgId)}/actions`),
  confirm: (orgId: string, actionId: string) => api.post<LoxleyAction>(`${base(orgId)}/actions/${actionId}/confirm`),
  cancel: (orgId: string, actionId: string) =>
    api.post<{ cancelled: boolean }>(`${base(orgId)}/actions/${actionId}/cancel`),
  myKeys: (orgId: string) => api.get<ConnectorKey[]>(`${base(orgId)}/keys`),
  createKey: (orgId: string, body: { name: string; scope: "read" | "read_write"; lifetime_days: number }) =>
    api.post<CreatedConnectorKey>(`${base(orgId)}/keys`, body),
  revokeKey: (orgId: string, keyId: string) => api.delete<{ revoked: number }>(`${base(orgId)}/keys/${keyId}`),
  admin: (orgId: string) => api.get<LoxleyAdmin>(`${base(orgId)}/admin`),
  assignSeat: (orgId: string, userId: string) =>
    api.put<{ seats: LoxleySeat[]; settings: LoxleySettings }>(`${base(orgId)}/admin/seats/${userId}`),
  removeSeat: (orgId: string, userId: string) =>
    api.delete<{ seats: LoxleySeat[]; settings: LoxleySettings }>(`${base(orgId)}/admin/seats/${userId}`),
  setConnectors: (orgId: string, enabled: boolean) =>
    api.put<LoxleySettings>(`${base(orgId)}/admin/connectors`, { enabled }),
  guardrails: (orgId: string) => api.get<LoxleyGuardrails>(`${base(orgId)}/admin/guardrails`),
  saveGuardrails: (orgId: string, rules: GuardrailRules) =>
    api.put<LoxleyGuardrails>(`${base(orgId)}/admin/guardrails`, { rules }),
  adminRevokeKey: (orgId: string, keyId: string) =>
    api.delete<{ revoked: number }>(`${base(orgId)}/admin/keys/${keyId}`),
  oauthDetails: (params: Record<string, string>) =>
    api.get<OAuthDetails>(`/oauth/authorize/details?${new URLSearchParams(params).toString()}`),
  oauthApprove: (params: Record<string, string>, approve: boolean, scope: "read" | "read_write") =>
    api.post<{ redirect: string }>("/oauth/authorize/approve", { params, approve, scope }),
};
